import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUserGear, faInfoCircle, faRightToBracket } from "@fortawesome/free-solid-svg-icons";
import { useStaffAuth } from "../store/staff";
import authSession from "../lib/authSession";
import { goAfterLogin } from "../lib/roles";

const inputCls =
  "w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-800 outline-none transition focus:border-[#16213A] focus:ring-2 focus:ring-[#16213A]/15";
const labelCls =
  "mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500";

/**
 * Staff login — POST /api/staff/auth/login.
 * Separate from the admin login; on success the staff token is stored and the
 * user is sent to the staff dashboard.
 */
const StaffLogin = () => {
  const navigate = useNavigate();
  const {
    staffLogin,
    staffLoginLoading,
    staffLoginError,
    staffForgotPassword,
    staffAuthActionLoading,
  } = useStaffAuth();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [notice, setNotice] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setNotice("");
    const res = await staffLogin(email.trim(), password);
    if (res.success) {
      authSession.persistIdentity("staff", { email: email.trim(), user: res.user });
      // Shared, role-aware redirect (the same map the route guards use).
      goAfterLogin(navigate, { role: "staff" });
    }
  };

  const handleForgot = async () => {
    if (!email.trim()) {
      setNotice("Enter your email address first, then click Forgot password.");
      return;
    }
    const res = await staffForgotPassword({ email: email.trim() });
    setNotice(
      res.success
        ? "If that account exists, a password-reset link has been sent."
        : res.error || "Could not send the reset email."
    );
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#f5f7fa] px-4">
      <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
        <div className="mb-6 flex flex-col items-center text-center">
          <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-[#16213A] text-white">
            <FontAwesomeIcon icon={faUserGear} className="text-xl" />
          </div>
          <h1 className="m-0 text-2xl font-bold tracking-tight text-slate-900">Staff Login</h1>
          <p className="m-0 mt-1 text-sm text-slate-500">
            Sign in to manage Exam PINs, purchases and deliveries.
          </p>
        </div>

        {(staffLoginError || notice) && (
          <div
            className={`mb-4 flex items-start gap-2 rounded-lg border px-3 py-2.5 text-sm ${
              staffLoginError
                ? "border-red-200 bg-red-50 text-red-700"
                : "border-slate-200 bg-slate-50 text-slate-600"
            }`}
          >
            <FontAwesomeIcon icon={faInfoCircle} className="mt-0.5" />
            <span>{staffLoginError || notice}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className={labelCls}>Email Address</label>
            <input
              type="email"
              className={inputCls}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="staff@filloplms.com"
              required
            />
          </div>
          <div>
            <label className={labelCls}>Password</label>
            <input
              type="password"
              className={inputCls}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
            />
          </div>

          <button
            type="submit"
            disabled={staffLoginLoading}
            className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-lg bg-[#16213A] px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-[#203052] disabled:opacity-70"
          >
            {staffLoginLoading ? (
              "Signing in…"
            ) : (
              <>
                <FontAwesomeIcon icon={faRightToBracket} /> Sign in
              </>
            )}
          </button>
        </form>

        <button
          type="button"
          onClick={handleForgot}
          disabled={staffAuthActionLoading}
          className="mt-4 w-full text-center text-xs font-semibold text-[#16213A] hover:underline disabled:opacity-60"
        >
          Forgot password?
        </button>
      </div>
    </div>
  );
};

export default StaffLogin;
